import { EffectOnTargetStatus, type StudyMissionSelectDto } from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Skeleton } from '@components/ui/skeleton';
import { cn } from '@lib/utils';
import { CrosshairIcon } from 'lucide-react';
import type { FC } from 'react';
import { MeiaMissionFilterPills } from './meia-mission-filter-pills.component';
import {
  EFFECT_ON_TARGET_STATUS_LEGEND,
  formatOverrideAuthorName,
  formatPkEstimate,
  getEffectOnTargetStatusBadgeVariant,
  getEffectOnTargetStatusDescription,
  getEffectOnTargetStatusLabel,
} from './meia-p3-engagement.util';
import { formatRunCaseDate } from './meia-p4-run-cases.util';
import {
  type BriefTargetCoverageRow,
  type BriefTargetEffectConfidence,
  getBriefTargetEffectSummary,
} from './meia-p5-brief.util';

type BriefTargetCoverageTileProps = {
  countByMissionId?: Map<string, number>;
  isLoading: boolean;
  missions: StudyMissionSelectDto[];
  onSelectMission: (missionId: string) => void;
  rows: BriefTargetCoverageRow[];
  selectedMissionId: string;
  totalCount: number;
};

export const BriefTargetCoverageTile: FC<BriefTargetCoverageTileProps> = (props) => {
  const { countByMissionId, isLoading, missions, onSelectMission, rows, selectedMissionId, totalCount } = props;
  const summary = getBriefTargetEffectSummary(rows);

  if (isLoading) {
    return (
      <Tile header={{ title: 'Effect on Target' }}>
        <Skeleton className="h-40 w-full" />
      </Tile>
    );
  }

  return (
    <Tile
      contentClassName="gap-3"
      header={{
        // The architecture-alternative picker is intentionally hidden — per-alternative
        // Effect-on-Target scoring is deferred (AZ-452); every target is scored study-level.
        title: (
          <span className="inline-flex items-center gap-2">
            <CrosshairIcon className="size-4 text-primary" />
            Effect on Target
            <TooltipComponent>
              How the selected alternative moves each target through Suppressed, Degraded, Contested, or Unengaged —
              derived from authored task engagements. Decision-maker overrides replace the model status where present.
            </TooltipComponent>
          </span>
        ),
      }}
    >
      <MeiaMissionFilterPills
        countByMissionId={countByMissionId}
        missions={missions}
        onSelect={onSelectMission}
        selectedMissionId={selectedMissionId}
        totalCount={totalCount}
      />
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
        <TargetEffectSummaryItem
          label="Suppressed"
          status={EffectOnTargetStatus.SUPPRESSED}
          tone="green"
          value={summary[EffectOnTargetStatus.SUPPRESSED]}
        />
        <TargetEffectSummaryItem
          label="Contested"
          status={EffectOnTargetStatus.CONTESTED}
          tone="yellow"
          value={summary[EffectOnTargetStatus.CONTESTED]}
        />
        <TargetEffectSummaryItem
          label="Degraded"
          status={EffectOnTargetStatus.DEGRADED}
          tone="blue"
          value={summary[EffectOnTargetStatus.DEGRADED]}
        />
        <TargetEffectSummaryItem
          label="Unengaged"
          status={EffectOnTargetStatus.UNENGAGED}
          value={summary[EffectOnTargetStatus.UNENGAGED]}
        />
        <TooltipComponent
          className="whitespace-pre-line"
          contentClassName="max-w-sm"
          triggerClassName="ml-auto"
        >
          {EFFECT_ON_TARGET_STATUS_LEGEND}
        </TooltipComponent>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-gray-500">No target engagements are available yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          {rows.map((row) => (
            <TargetCoverageCard
              key={row.code}
              row={row}
            />
          ))}
        </div>
      )}
    </Tile>
  );
};

const TargetCoverageCard: FC<{ row: BriefTargetCoverageRow }> = (props) => {
  const { row } = props;
  const hasOverride = Boolean(row.decisionMakerOverride);

  return (
    <div className="rounded-md border border-gray-200 px-3 py-2 text-xs">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-gray-900">{row.label}</div>
          <div className="font-mono text-[10px] text-gray-400">{row.code}</div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <TooltipComponent
            trigger={
              <span className="inline-flex cursor-help">
                <Badge variant={getEffectOnTargetStatusBadgeVariant(row.effectiveStatus)}>
                  {getEffectOnTargetStatusLabel(row.effectiveStatus)}
                </Badge>
              </span>
            }
          >
            {getEffectOnTargetStatusDescription(row.effectiveStatus)}
          </TooltipComponent>
          {hasOverride && (
            <TooltipComponent
              trigger={
                <span className="inline-flex cursor-help">
                  <Badge
                    size="xs"
                    variant="outline"
                  >
                    DM override
                  </Badge>
                </span>
              }
            >
              A decision maker pinned this status over the model result — the rationale is shown on the card.
            </TooltipComponent>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <TooltipComponent
            trigger={
              <span className="inline-flex cursor-help">
                <Badge
                  size="xs"
                  variant={getEffectOnTargetStatusBadgeVariant(row.modelStatus)}
                >
                  Model: {getEffectOnTargetStatusLabel(row.modelStatus)}
                </Badge>
              </span>
            }
          >
            {getEffectOnTargetStatusDescription(row.modelStatus)}
          </TooltipComponent>
          <Badge
            size="xs"
            variant="secondary"
          >
            {row.engagementCount} {row.engagementCount === 1 ? 'engagement' : 'engagements'}
          </Badge>
          <TooltipComponent
            trigger={
              <span className="inline-flex cursor-help">
                <Badge
                  size="xs"
                  variant="outline"
                >
                  {formatPkEstimate(row.maxPkEstimate)}
                </Badge>
              </span>
            }
          >
            Highest probability-of-kill estimate across this target&rsquo;s engagements.
          </TooltipComponent>
        </div>

        <TargetConfidenceBar confidence={row.confidence} />

        {row.decisionMakerOverride && (
          <div className="rounded-sm border border-gray-200 bg-gray-50 p-2 text-gray-600">
            <div className="mb-1 flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-gray-700">Decision maker</span>
              <Badge
                size="xs"
                variant={getEffectOnTargetStatusBadgeVariant(row.decisionMakerOverride.status)}
              >
                {getEffectOnTargetStatusLabel(row.decisionMakerOverride.status)}
              </Badge>
            </div>
            <p>{row.decisionMakerOverride.rationale}</p>
            <p className="mt-1 text-[11px] text-gray-400">
              — {formatOverrideAuthorName(row.decisionMakerOverride)} ·{' '}
              {formatRunCaseDate(row.decisionMakerOverride.updatedAt)}
            </p>
          </div>
        )}

        <div className="text-gray-500">
          {row.technologyNames.length > 0 ? row.technologyNames.join(', ') : 'No technology coverage'}
        </div>
      </div>
    </div>
  );
};

const TargetConfidenceBar: FC<{ confidence: BriefTargetEffectConfidence }> = (props) => {
  const { confidence } = props;

  return (
    <TooltipComponent
      trigger={
        <div className="flex cursor-help items-center gap-2">
          <div className="h-1.5 min-w-20 flex-1 rounded-full bg-gray-100">
            <div className={cn('h-full rounded-full', confidenceBarClassNameByConfidence[confidence])} />
          </div>
          <span className="w-14 text-right text-[10px] font-medium text-gray-500">
            {confidenceLabelByConfidence[confidence]}
          </span>
        </div>
      }
    >
      Confidence in the model status, derived from the strength of this target&rsquo;s best engagement pK.
    </TooltipComponent>
  );
};

type TargetEffectSummaryItemProps = {
  label: string;
  status: EffectOnTargetStatus;
  tone?: 'blue' | 'default' | 'green' | 'yellow';
  value: number;
};

const TargetEffectSummaryItem: FC<TargetEffectSummaryItemProps> = (props) => {
  const { label, status, tone = 'default', value } = props;

  return (
    <TooltipComponent
      trigger={
        <span className="inline-flex cursor-help items-center gap-1">
          <span
            className={cn(
              'size-2.5 rounded-full',
              tone === 'blue' && 'bg-primary',
              tone === 'default' && 'bg-gray-400',
              tone === 'green' && 'bg-emerald-500',
              tone === 'yellow' && 'bg-amber-500',
            )}
          />
          {label}
          <span className="font-mono font-semibold text-gray-900">{value}</span>
        </span>
      }
    >
      {getEffectOnTargetStatusDescription(status)}
    </TooltipComponent>
  );
};

const confidenceBarClassNameByConfidence: Record<BriefTargetEffectConfidence, string> = {
  high: 'w-full bg-emerald-500',
  low: 'w-1/3 bg-rose-500',
  medium: 'w-2/3 bg-amber-500',
  unknown: 'w-1/6 bg-gray-300',
};

const confidenceLabelByConfidence: Record<BriefTargetEffectConfidence, string> = {
  high: 'High',
  low: 'Low',
  medium: 'Medium',
  unknown: 'Unknown',
};
